/**
 * ToolCardDock — full eleven-card dispatcher for the right-rail
 * layer-family ToolCards (WP-12 — `docs/results/WP-12-toolcarddock-
 * integration-design-brief.md`).
 *
 * The dock implements the masterplan §6.3 / §6.5 right-rail:
 *
 *   - One Card mounted at a time (the **single-drawer rule** from
 *     `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5).
 *   - Card chosen by `useViewerStore.sceneSelection.toolCardKind`,
 *     resolved through `app/sceneExplorer.getToolCardKindFor` so the
 *     same selection contract drives the SceneTreePanel context menu,
 *     deep-link dispatchers, and engine pickers.
 *   - When nothing is selected the dock renders `<EmptyToolCard />`
 *     so the rail communicates the empty state instead of being
 *     blank (`UIUX_DETAILED_SURFACE_STUDY.md` empty-state pattern).
 *   - Esc while focus is inside the dock clears the selection and
 *     returns focus to the scene tree panel — matches the canonical
 *     "Esc closes drawers" rule from `DESIGN_INVENTORY.md` §"Interaction
 *     Inventory".
 *   - Each rendered card is wrapped in a stable
 *     `data-testid="tool-card-<kind>"` so E2E selectors stay
 *     decoupled from per-Card internals.
 *
 * The dispatcher is intentionally a single `switch` on
 * `toolCardKind`. Adding a new family is one new `case` plus a
 * `data-testid` — no other surface change required.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { useViewerStore } from '@/state/useViewerStore';
import { useViewerSlice } from '@/state/useViewerSlice';
import {
  TOOL_CARD_REQUEST_EVENT,
  type SceneSelection,
  type ToolCardRequestDetail,
} from '@/app/sceneSelection';
import { getToolCardKindFor, type ToolCardKind } from '@/app/sceneExplorer';
import { tokens } from '@/design';
import { BasemapToolCard } from '@/components/basemap/BasemapToolCard';
import { RasterToolCard } from '@/components/raster/RasterToolCard';
import { TerrainToolCard } from '@/components/terrain/TerrainToolCard';
import { VectorToolCard } from '@/components/vector/VectorToolCard';
import {
  PointCloudToolCard,
  type PointCloudToolCardProps,
} from '@/components/pointcloud/PointCloudToolCard';
import { ThreeDModelToolCard } from '@/components/three-d/ThreeDModelToolCard';
import { SectionAndClipToolCard } from '@/components/three-d/SectionAndClipToolCard';
import {
  GeophysicsToolCard,
  type GeophysicsRasterAdapter,
  type GeophysicsStyleState,
} from '@/components/geophysics/GeophysicsToolCard';
import {
  BathymetryToolCard,
  type BathymetryAdapter,
  type BathymetryStyleState,
} from '@/components/hydrographic/BathymetryToolCard';
import { SensorOverlayToolCard } from '@/components/sensors/SensorOverlayToolCard';
import type { SensorOverlayState } from '@/components/sensors/sensorModel';
import { AIReviewToolCard } from '@/components/ai/AIReviewToolCard';
import { ReadinessToolCard } from '@/components/readiness/ReadinessToolCard';
import type { VectorStyleHook } from '@/components/vector/VectorToolCard';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { EmptyToolCard } from './EmptyToolCard';
import { EnvironmentToolCard } from '@/modules/weather';

// ---------------------------------------------------------------------------
// Public props
// ---------------------------------------------------------------------------

export interface ToolCardDockProps {
  /** Map container forwarded to cards that need to drive MapLibre. */
  container?: HTMLElement | null;
  /** All renderable layers; the dock picks the active one by selection. */
  layers?: readonly Layer[];
  /** Cursor lng/lat forwarded to BasemapToolCard. */
  coordinate?: { lon: number; lat: number } | null;
  scaleDenominator?: number | null;
  bearingDeg?: number | null;
  onResetView?: () => void;
  onResetNorth?: () => void;
  onOpenGeocoder?: () => void;
  onShareView?: () => void;
  /**
   * Permission-denied banner copy. Forwarded to whichever card is
   * currently active so a single permission failure surfaces once,
   * not eleven times.
   */
  permissionDenied?: string | null;
  /** Read-only flag from the gTwin permission model. Forwarded to
   *  every card that exposes the prop. */
  readOnly?: boolean;
  /** Active point-cloud command adapter, normally the Giro3D adapter. */
  pointCloudAdapter?: PointCloudToolCardProps['adapter'];
  /** Active 3D model command adapter, normally the Giro3D adapter. */
  threeDModelAdapter?: import('@/components/three-d/ThreeDModelToolCard').ThreeDModelCommandAdapter | null;
  /**
   * Optional override for the focus target Esc returns to. Defaults
   * to the SceneTreePanel root resolved by the same selector the
   * `focus-layers` shortcut uses (`#layers` /
   * `[aria-label="Unified scene tree"]`).
   */
  onEscapeFocusReturn?: () => void;
  'data-testid'?: string;
}

// ---------------------------------------------------------------------------
// Geophysics / Bathymetry / Sensor — controlled-state wrappers
//
// The Cards 7 (Geophysics, Bathymetry) and 8 (Sensor overlay) require
// fully-controlled style state. The viewer store does not yet ship a
// dedicated slice for these — promoting them to the store is tracked
// for a follow-up WP and is intentionally out of WP-12's scope. The
// dispatcher therefore owns local state with the documented defaults
// from each Card, so the Cards mount end-to-end without forcing the
// host shell to reach into per-family details.
// ---------------------------------------------------------------------------

const DEFAULT_GEOPHYSICS_STYLE: GeophysicsStyleState = {
  opacity: 1,
  contrast: 1,
  gain: 1,
  colorRamp: 'greyscale',
  blendMode: 'normal',
  depthM: 0,
  timeMs: 0,
  channel: 0,
};

const DEFAULT_BATHYMETRY_STYLE: BathymetryStyleState = {
  opacity: 1,
  colorRamp: 'depth-blue',
  verticalExaggeration: 1,
  contourIntervalM: 10,
  isobathsVisible: true,
  soundingDensity: 'normal',
};

interface GeophysicsHostProps {
  readonly layer: Layer | null;
  readonly container?: HTMLElement | null;
}

function GeophysicsHost({ layer, container }: GeophysicsHostProps) {
  const [style, setStyle] = useState<GeophysicsStyleState>(
    DEFAULT_GEOPHYSICS_STYLE,
  );
  const adapter = useMemo(
    () => createGeophysicsRasterAdapter(container, layer),
    [container, layer],
  );
  useEffect(() => {
    setStyle(DEFAULT_GEOPHYSICS_STYLE);
  }, [layer?.id]);
  if (!layer) {
    return (
      <GeophysicsToolCard
        layerId="empty"
        layerLabel="Select a geophysics layer"
        style={style}
        onStyleChange={setStyle}
        adapter={adapter}
        state="empty"
      />
    );
  }
  return (
    <GeophysicsToolCard
      layerId={layer.id}
      layerLabel={layer.label ?? layer.id}
      style={style}
      onStyleChange={setStyle}
      adapter={adapter}
    />
  );
}

interface BathymetryHostProps {
  readonly layer: Layer | null;
  readonly container?: HTMLElement | null;
}

function BathymetryHost({ layer, container }: BathymetryHostProps) {
  const [style, setStyle] = useState<BathymetryStyleState>(
    DEFAULT_BATHYMETRY_STYLE,
  );
  const adapter = useMemo(
    () => createBathymetryRasterAdapter(container, layer),
    [container, layer],
  );
  useEffect(() => {
    setStyle(DEFAULT_BATHYMETRY_STYLE);
  }, [layer?.id]);
  if (!layer) {
    return (
      <BathymetryToolCard
        layerId="empty"
        layerLabel="Select a bathymetry layer"
        style={style}
        onStyleChange={setStyle}
        adapter={adapter}
        state="empty"
      />
    );
  }
  return (
    <BathymetryToolCard
      layerId={layer.id}
      layerLabel={layer.label ?? layer.id}
      style={style}
      onStyleChange={setStyle}
      adapter={adapter}
    />
  );
}

interface SensorHostProps {
  readonly layer: Layer | null;
}

function SensorHost({ layer }: SensorHostProps) {
  const state = useMemo<SensorOverlayState>(
    () => ({
      title: layer?.label ?? 'Live overlay',
      sourceId: layer?.id,
      permission: 'granted',
      connectivity: 'online',
    }),
    [layer],
  );
  return <SensorOverlayToolCard state={state} />;
}

// ---------------------------------------------------------------------------
// Default focus-return target — re-uses the selector the `focus-layers`
// keyboard shortcut already exercises so Esc lands on the same spot the
// user came from.
// ---------------------------------------------------------------------------

export function defaultEscapeFocusReturn(): void {
  if (typeof document === 'undefined') return;
  const root =
    document.getElementById('layers') ??
    document.querySelector('[aria-label="Unified scene tree"]');
  const focusable = root?.querySelector<HTMLElement>(
    'input,button,[tabindex]',
  );
  focusable?.focus();
}

// ---------------------------------------------------------------------------
// Keyboard handler — exported so the WP-12 dispatcher unit test can
// invoke it without depending on a React DOM event runtime. The dock's
// `onKeyDown` is a thin React-side wrapper that calls this helper.
// ---------------------------------------------------------------------------

export interface DockEscapeArgs {
  /** Native key string (`event.key`). */
  readonly key: string;
  /**
   * Bound `clearSceneSelection` from the viewer store. Pass
   * `useViewerStore.getState().clearSceneSelection` from callers; the
   * helper calls it with `'keyboard'` so the audit trail records the
   * Esc-from-dock origin.
   */
  readonly clearSceneSelection: (source?: 'keyboard') => unknown;
  /** Optional focus-return callback. Defaults to {@link defaultEscapeFocusReturn}. */
  readonly focusReturn?: () => void;
  /** Optional `event.stopPropagation()` hook. */
  readonly stopPropagation?: () => void;
}

export function handleDockEscape(args: DockEscapeArgs): boolean {
  if (args.key !== 'Escape') return false;
  args.stopPropagation?.();
  args.clearSceneSelection('keyboard');
  (args.focusReturn ?? defaultEscapeFocusReturn)();
  return true;
}

export function buildSceneSelectionFromToolCardRequest(
  detail: ToolCardRequestDetail,
): SceneSelection {
  const resolvedToolCardKind = resolveToolCardKindFromRequest(detail);
  const hasFeature = detail.featureId !== undefined && detail.featureId !== null;
  const hasIndexNode = typeof detail.indexNodeId === 'string' && detail.indexNodeId.length > 0;
  const hasLayer = typeof detail.layerId === 'string' && detail.layerId.length > 0;
  const hasSceneItem = typeof detail.sceneItemId === 'string' && detail.sceneItemId.length > 0;
  const kind: SceneSelection['kind'] = hasFeature
    ? 'feature'
    : hasIndexNode
      ? 'indexNode'
      : hasLayer
        ? 'layer'
        : hasSceneItem
          ? 'sceneItem'
          : 'cleared';
  if (kind === 'cleared') {
    return {
      kind: 'cleared',
      source: detail.source ?? 'tool-card',
    };
  }
  return {
    kind,
    layerId: detail.layerId,
    layerKind: detail.layerKind,
    sceneItemId: detail.sceneItemId ?? detail.layerId,
    indexNodeId: detail.indexNodeId,
    nodeId: detail.indexNodeId,
    featureId: detail.featureId,
    toolCardKind: resolvedToolCardKind,
    source: detail.source ?? 'tool-card',
  };
}

function resolveToolCardKindFromRequest(detail: ToolCardRequestDetail): ToolCardKind {
  if (detail.layerKind) {
    const fromLayerKind = getToolCardKindFor({ layerKind: detail.layerKind });
    if (fromLayerKind !== 'gtwin') return fromLayerKind;
  }
  if (detail.toolCardKind) return detail.toolCardKind;
  return 'gtwin';
}

function setMapLibreRasterPaint(
  container: HTMLElement | null | undefined,
  layerId: string,
  property:
    | 'raster-opacity'
    | 'raster-contrast'
    | 'raster-brightness-max'
    | 'raster-saturation',
  value: number,
): void {
  if (!container) return;
  const map = getMapLibreHostHandle(container);
  if (!map) return;
  const mapLayerId = `layer-${layerId}`;
  if (!map.getLayer(mapLayerId)) return;
  try {
    map.setPaintProperty(mapLayerId, property, value);
  } catch {
    /* maplibre property support can vary by style layer */
  }
}

function createVectorStyleHook(
  container: HTMLElement | null | undefined,
): VectorStyleHook | null {
  if (!container) return null;
  const map = getMapLibreHostHandle(container);
  if (!map) return null;
  return {
    setVectorPaint(layerId, delta) {
      const lineId = `layer-${layerId}-line`;
      const fillId = `layer-${layerId}-fill`;
      const singleId = `layer-${layerId}`;
      try {
        if (map.getLayer(lineId)) {
          if (delta.lineColor !== undefined) {
            map.setPaintProperty(lineId, 'line-color', delta.lineColor);
          }
          if (delta.lineWidth !== undefined) {
            map.setPaintProperty(lineId, 'line-width', delta.lineWidth);
          }
        } else if (map.getLayer(singleId)) {
          if (delta.lineColor !== undefined) {
            map.setPaintProperty(singleId, 'line-color', delta.lineColor);
          }
          if (delta.lineWidth !== undefined) {
            map.setPaintProperty(singleId, 'line-width', delta.lineWidth);
          }
        }
        if (map.getLayer(fillId)) {
          if (delta.fillColor !== undefined) {
            map.setPaintProperty(fillId, 'fill-color', delta.fillColor);
          }
          if (delta.fillOpacity !== undefined) {
            map.setPaintProperty(fillId, 'fill-opacity', delta.fillOpacity);
          }
        }
      } catch {
        /* map style may not expose all vector paint keys */
      }
    },
    setVectorFilter(layerId, filter) {
      for (const candidate of [
        `layer-${layerId}-line`,
        `layer-${layerId}-fill`,
        `layer-${layerId}`,
      ]) {
        try {
          if (map.getLayer(candidate)) {
            map.setFilter(candidate, filter as any);
          }
        } catch {
          /* ignore invalid filter expressions for unsupported layers */
        }
      }
    },
    setVectorLabels(layerId, options) {
      const labelId = `layer-${layerId}-label`;
      const sourceId = `src-${layerId}`;
      try {
        if (!options.enabled || !options.field) {
          if (map.getLayer(labelId)) map.removeLayer(labelId);
          return;
        }
        if (!map.getSource(sourceId)) return;
        if (map.getLayer(labelId)) map.removeLayer(labelId);
        map.addLayer({
          id: labelId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'text-field': ['to-string', ['get', options.field]],
            'text-size': 12,
            'text-anchor': 'top',
            'text-offset': [0, 0.5],
            'text-allow-overlap': false,
          },
        });
      } catch {
        /* map style can reject symbol layers on some source types */
      }
    },
    getVectorFeatures(layerId) {
      const sourceId = `src-${layerId}`;
      try {
        const source = map.getSource(sourceId) as
          | { _data?: { features?: unknown } }
          | undefined;
        const features = source?._data?.features;
        if (Array.isArray(features)) {
          return features as import('@geosoft/viewer-sdk').VectorExportFeature[];
        }
      } catch {
        /* fall through to empty */
      }
      return [];
    },
  };
}

export function toMapLibreRasterContrast(value: number): number {
  return Math.max(-1, Math.min(1, value - 1));
}

export function toMapLibreRasterSaturation(value: number): number {
  return Math.max(-1, Math.min(1, value - 1));
}

function applyBasemapCardStateToMap(
  container: HTMLElement | null | undefined,
  state: {
    readonly opacity: number;
    readonly visible: boolean;
    readonly brightness: number;
    readonly contrast: number;
  },
): void {
  if (!container) return;
  const map = getMapLibreHostHandle(container);
  if (!map) return;
  const style = map.getStyle?.();
  const layers = style?.layers ?? [];
  const targetLayers = layers.filter(
    (entry) => entry.id.startsWith('basemap-') && entry.type === 'raster',
  );
  if (targetLayers.length === 0) return;
  const opacity = Math.max(0, Math.min(1, state.visible ? state.opacity : 0));
  const brightness = Math.max(0, Math.min(2, state.brightness));
  const contrast = toMapLibreRasterContrast(state.contrast);
  for (const targetLayer of targetLayers) {
    try {
      map.setPaintProperty(targetLayer.id, 'raster-opacity', opacity);
      map.setPaintProperty(targetLayer.id, 'raster-brightness-max', brightness);
      map.setPaintProperty(targetLayer.id, 'raster-contrast', contrast);
    } catch {
      /* best-effort; basemap style layers can vary by provider */
    }
  }
}

function applyRasterCardStateToMap(
  container: HTMLElement | null | undefined,
  layerId: string | undefined,
  state: {
    readonly opacity: number;
    readonly brightness: number;
    readonly contrast: number;
    readonly saturation: number;
  },
): void {
  if (!layerId) return;
  setMapLibreRasterPaint(
    container,
    layerId,
    'raster-opacity',
    Math.max(0, Math.min(1, state.opacity)),
  );
  setMapLibreRasterPaint(
    container,
    layerId,
    'raster-brightness-max',
    Math.max(0, Math.min(2, state.brightness)),
  );
  setMapLibreRasterPaint(
    container,
    layerId,
    'raster-contrast',
    toMapLibreRasterContrast(state.contrast),
  );
  setMapLibreRasterPaint(
    container,
    layerId,
    'raster-saturation',
    toMapLibreRasterSaturation(state.saturation),
  );
}

function applyTerrainCardStateToMap(
  container: HTMLElement | null | undefined,
  layerId: string | undefined,
  opacity: number,
): void {
  if (!container || !layerId) return;
  const map = getMapLibreHostHandle(container);
  if (!map) return;
  const mapLayerId = `layer-${layerId}`;
  if (!map.getLayer(mapLayerId)) return;
  const clamped = Math.max(0, Math.min(1, opacity));
  const props: Array<'raster-opacity' | 'fill-opacity' | 'line-opacity'> = [
    'raster-opacity',
    'fill-opacity',
    'line-opacity',
  ];
  for (const prop of props) {
    try {
      map.setPaintProperty(mapLayerId, prop, clamped);
    } catch {
      /* unsupported property for this terrain style layer */
    }
  }
}

function createGeophysicsRasterAdapter(
  container: HTMLElement | null | undefined,
  layer: Layer | null,
): GeophysicsRasterAdapter | undefined {
  const layerId = layer?.id;
  if (!layerId) return undefined;
  return {
    setOpacity(_targetLayerId, opacity) {
      const clamped = Math.max(0, Math.min(1, opacity));
      setMapLibreRasterPaint(container, layerId, 'raster-opacity', clamped);
    },
    setContrast(_targetLayerId, contrast) {
      const clamped = Math.max(-1, Math.min(1, contrast));
      setMapLibreRasterPaint(container, layerId, 'raster-contrast', clamped);
    },
    setGain(_targetLayerId, gain) {
      const clamped = Math.max(0, Math.min(4, gain));
      setMapLibreRasterPaint(
        container,
        layerId,
        'raster-brightness-max',
        Math.max(0, Math.min(2, clamped / 2)),
      );
    },
    setColorRamp() {},
    setBlendMode() {},
    setDepth() {},
    setTime() {},
    setChannel() {},
  };
}

function createBathymetryRasterAdapter(
  container: HTMLElement | null | undefined,
  layer: Layer | null,
): BathymetryAdapter | undefined {
  const layerId = layer?.id;
  if (!layerId) return undefined;
  return {
    setOpacity(_targetLayerId, opacity) {
      const clamped = Math.max(0, Math.min(1, opacity));
      setMapLibreRasterPaint(container, layerId, 'raster-opacity', clamped);
    },
    setColorRamp() {},
    setVerticalExaggeration() {},
    setContourInterval() {},
    setIsobathsVisible() {},
    setSoundingDensity() {},
  };
}

function normalizeSelectionIdentity(
  value: unknown,
): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return undefined;
}

export function resolveThreeDSelectionSnapshot(
  sceneSelection: SceneSelection,
  pickedSelection:
    | import('@/components/three-d/ThreeDModelToolCard').ThreeDFeatureSelection
    | null,
): import('@/components/three-d/ThreeDModelToolCard').ThreeDFeatureSelection | undefined {
  const fallbackFeatureId = normalizeSelectionIdentity(sceneSelection.featureId);
  const fallbackNodeId = normalizeSelectionIdentity(
    sceneSelection.indexNodeId ?? sceneSelection.nodeId,
  );
  const pickedFeatureId = normalizeSelectionIdentity(pickedSelection?.featureId);
  const pickedNodeId = normalizeSelectionIdentity(pickedSelection?.nodeId);
  const hasSnapshotData = Boolean(
    pickedSelection
    || fallbackFeatureId
    || fallbackNodeId,
  );
  if (!hasSnapshotData) return undefined;
  return {
    ...(pickedSelection ?? {}),
    featureId: pickedFeatureId ?? fallbackFeatureId,
    nodeId: pickedNodeId ?? fallbackNodeId,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ToolCardDock({
  container,
  layers,
  coordinate,
  scaleDenominator,
  bearingDeg,
  onResetView,
  onResetNorth,
  onOpenGeocoder,
  onShareView,
  permissionDenied,
  readOnly,
  pointCloudAdapter,
  threeDModelAdapter,
  onEscapeFocusReturn,
  'data-testid': testId,
}: ToolCardDockProps) {
  const sceneSelection = useViewerSlice((s) => s.sceneSelection);
  const basemapToolCard = useViewerSlice((s) => s.basemapToolCard);
  const rasterToolCard = useViewerSlice((s) => s.rasterToolCard);
  const terrainToolCard = useViewerSlice((s) => s.terrainToolCard);
  // G20 — companion-layer wiring for the terrain tool card (hillshade /
  // slope / aspect / contours toggles drive these layers' visibility).
  const visibleLayerIds = useViewerSlice((s) => s.visibleLayerIds);
  const setVisible = useViewerStore((s) => s.setVisible);
  const setSceneSelection = useViewerStore((s) => s.setSceneSelection);
  const clearSceneSelection = useViewerStore((s) => s.clearSceneSelection);

  const dockRef = useRef<HTMLDivElement | null>(null);
  const dockId = useId();

  // Listen for `viewer:tool-card-request` events emitted by the
  // SceneTreePanel context menu (WP-2) and by cards that pivot to
  // sibling families (e.g. SensorOverlayToolCard's "linked target"
  // chip). Translate each into a scene-selection write so the
  // dispatcher stays a single switch.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ToolCardRequestDetail>).detail;
      if (!detail) return;
      setSceneSelection(buildSceneSelectionFromToolCardRequest(detail));
    };
    window.addEventListener(TOOL_CARD_REQUEST_EVENT, handler);
    return () => {
      window.removeEventListener(TOOL_CARD_REQUEST_EVENT, handler);
    };
  }, [setSceneSelection]);

  // Esc while focus is inside the dock clears the scene selection and
  // returns focus to the scene tree panel. Scoped to the dock root so
  // global Esc handling outside the dock is unaffected. The actual
  // logic lives in {@link handleDockEscape} so it can be tested in
  // the node test environment without a React DOM runtime.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      handleDockEscape({
        key: event.key,
        clearSceneSelection: clearSceneSelection as (
          source?: 'keyboard',
        ) => unknown,
        focusReturn: onEscapeFocusReturn,
        stopPropagation: () => event.stopPropagation(),
      });
    },
    [clearSceneSelection, onEscapeFocusReturn],
  );

  const layer =
    layers?.find((l) => l.id === sceneSelection.layerId)
    ?? (sceneSelection.sceneItemId
      ? layers?.find((l) => l.id === sceneSelection.sceneItemId)
      : undefined)
    ?? null;
  // G30 — when the tree selection still carries `toolCardKind: gtwin` (e.g.
  // metadata.layerKind was `cog` before terrain-slot normalisation), promote
  // from the resolved catalog layer so terrain / raster / vector cards open.
  const layerDerivedToolCardKind = layer
    ? getToolCardKindFor(layer)
    : sceneSelection.layerKind
      ? getToolCardKindFor({ layerKind: sceneSelection.layerKind })
      : 'gtwin';
  const metadataDerivedToolCardKind = sceneSelection.layerKind
    ? getToolCardKindFor({ layerKind: sceneSelection.layerKind })
    : null;
  const shouldPromoteFromCatalog =
    layerDerivedToolCardKind !== 'gtwin'
    && (
      !sceneSelection.toolCardKind
      || sceneSelection.toolCardKind === 'gtwin'
      || layerDerivedToolCardKind === '3dtiles'
      || layerDerivedToolCardKind === 'bim'
      || (
        layerDerivedToolCardKind === 'terrain'
        && metadataDerivedToolCardKind !== 'terrain'
      )
    );
  const toolCardKind = shouldPromoteFromCatalog
    ? layerDerivedToolCardKind
    : sceneSelection.toolCardKind;
  const combinedPointCloudLayer = layers?.find(
    (candidate) =>
      candidate.kind === 'pointcloud'
      || Boolean(candidate.endpoints.potree || candidate.endpoints.copc || candidate.endpoints.pointcloud),
  ) ?? null;
  const combinedModelLayer = layers?.find(
    (candidate) =>
      candidate.kind === '3dtiles'
      || candidate.kind === 'bim'
      || Boolean(candidate.endpoints.tileset3d || candidate.endpoints.model),
  ) ?? null;
  const showCombinedDualCards = Boolean(combinedPointCloudLayer && combinedModelLayer);

  useEffect(() => {
    applyBasemapCardStateToMap(container, basemapToolCard);
  }, [container, basemapToolCard]);

  useEffect(() => {
    applyRasterCardStateToMap(
      container,
      toolCardKind === 'raster' ? sceneSelection.layerId : undefined,
      rasterToolCard,
    );
  }, [container, rasterToolCard, sceneSelection.layerId, toolCardKind]);

  useEffect(() => {
    applyTerrainCardStateToMap(
      container,
      toolCardKind === 'terrain' ? sceneSelection.layerId : undefined,
      terrainToolCard.opacity,
    );
  }, [container, sceneSelection.layerId, terrainToolCard.opacity, toolCardKind]);

  // Auto-focus the first interactive element inside the active card
  // when the rendered card kind changes. Mirrors the platform
  // accessibility plan ("Each card's primary CTA receives focus when
  // the card mounts"). Skipped when no card is mounted (empty state).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!toolCardKind || toolCardKind === 'gtwin') return;
    const root = dockRef.current;
    if (!root) return;
    // Defer to the next microtask so the just-mounted card has its
    // children attached before we look for a focus target.
    const handle = window.setTimeout(() => {
      const target = root.querySelector<HTMLElement>(
        'button:not([disabled]),[tabindex="0"],input:not([disabled])',
      );
      target?.focus();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [toolCardKind]);

  const card = renderCard({
    toolCardKind,
    sceneSelection,
    layer,
    layerKind: sceneSelection.layerKind,
    layers,
    visibleLayerIds,
    setVisible,
    container,
    coordinate,
    scaleDenominator,
    bearingDeg,
    onResetView,
    onResetNorth,
    onOpenGeocoder,
    onShareView,
    permissionDenied,
    readOnly,
    pointCloudAdapter,
    threeDModelAdapter,
    clearSceneSelection: clearSceneSelection as (source?: 'keyboard') => unknown,
  });
  const combinedSelection = resolveThreeDSelectionSnapshot(
    sceneSelection,
    threeDModelAdapter?.getLastPickedFeatureSelection?.() ?? null,
  );
  const combinedModelTree = threeDModelAdapter?.getModelTree?.();

  return (
    <div
      ref={dockRef}
      data-testid={testId ?? 'tool-card-dock'}
      role="region"
      aria-label="Layer tools"
      onKeyDown={onKeyDown}
      id={dockId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[3],
        padding: tokens.space[3],
        height: '100%',
        overflowY: 'auto',
        background: tokens.color.surface[0],
      }}
    >
      {showCombinedDualCards ? (
        <>
          <CardWrapper kind="pointcloud">
            <PointCloudToolCard
              adapter={pointCloudAdapter ?? null}
              layer={combinedPointCloudLayer}
              forceVisible
            />
          </CardWrapper>
          <CardWrapper kind="3dtiles">
            <ThreeDModelToolCard
              adapter={threeDModelAdapter ?? null}
              layer={combinedModelLayer}
              selection={combinedSelection}
              modelTree={combinedModelTree}
              forceVisible
            />
            <SectionAndClipToolCard
              adapter={threeDModelAdapter?.getSectionAndClipAdapter?.() ?? null}
              forceVisible
            />
          </CardWrapper>
          {toolCardKind !== 'pointcloud'
            && toolCardKind !== 'bim'
            && toolCardKind !== '3dtiles'
            ? card
            : null}
        </>
      ) : (
        card
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pure dispatch table — one Card per `toolCardKind`. Bathymetry lives
// under the `geophysics` family but renders the bathymetry-specific
// card when the underlying layer kind is `bathymetry`.
// ---------------------------------------------------------------------------

interface DispatchInput {
  readonly toolCardKind: string | undefined;
  readonly sceneSelection: SceneSelection;
  readonly layer: Layer | null;
  readonly layerKind: string | undefined;
  /** Full catalog — forwarded to cards that need cross-layer lookups (G20). */
  readonly layers?: readonly Layer[];
  readonly visibleLayerIds?: ReadonlySet<string>;
  readonly setVisible?: (layerId: string, visible: boolean) => void;
  readonly container?: HTMLElement | null;
  readonly coordinate?: { lon: number; lat: number } | null;
  readonly scaleDenominator?: number | null;
  readonly bearingDeg?: number | null;
  readonly onResetView?: () => void;
  readonly onResetNorth?: () => void;
  readonly onOpenGeocoder?: () => void;
  readonly onShareView?: () => void;
  readonly permissionDenied?: string | null;
  readonly readOnly?: boolean;
  readonly pointCloudAdapter?: PointCloudToolCardProps['adapter'];
  readonly threeDModelAdapter?: import('@/components/three-d/ThreeDModelToolCard').ThreeDModelCommandAdapter | null;
  readonly clearSceneSelection?: (source?: 'keyboard') => unknown;
}

function renderCard(input: DispatchInput): ReactNode {
  const {
    toolCardKind,
    sceneSelection,
    layer,
    layerKind,
    layers,
    visibleLayerIds,
    setVisible,
    container,
    coordinate,
    scaleDenominator,
    bearingDeg,
    onResetView,
    onResetNorth,
    onOpenGeocoder,
    onShareView,
    permissionDenied,
    readOnly,
    pointCloudAdapter,
    threeDModelAdapter,
    clearSceneSelection,
  } = input;

  switch (toolCardKind) {
    case 'basemap':
      return (
        <CardWrapper kind="basemap">
          <BasemapToolCard
            container={container ?? null}
            coordinate={coordinate ?? null}
            scaleDenominator={scaleDenominator}
            bearingDeg={bearingDeg}
            onResetView={onResetView}
            onResetNorth={onResetNorth}
            onOpenGeocoder={onOpenGeocoder}
            onShareView={onShareView}
            permissionDenied={permissionDenied}
          />
        </CardWrapper>
      );
    case 'raster':
      return (
        <CardWrapper kind="raster">
          <RasterToolCard
            layer={layer}
            permissionDenied={permissionDenied}
            readOnly={readOnly}
            isElevationLike={false}
          />
        </CardWrapper>
      );
    case 'terrain':
      return (
        <CardWrapper kind="terrain">
          <TerrainToolCard
            layer={layer}
            layers={layers}
            visibleLayerIds={visibleLayerIds}
            onSetLayerVisible={setVisible}
            permissionDenied={permissionDenied}
            readOnly={readOnly}
          />
        </CardWrapper>
      );
    case 'vector':
      return (
        <CardWrapper kind="vector">
          <VectorToolCard
            layer={layer}
            readOnly={readOnly}
            engine={createVectorStyleHook(container)}
            selectedFeatureId={sceneSelection.featureId ?? null}
          />
        </CardWrapper>
      );
    case 'pointcloud':
      return (
        <CardWrapper kind="pointcloud">
          <PointCloudToolCard
            adapter={pointCloudAdapter ?? null}
            layer={layer}
            forceVisible
          />
        </CardWrapper>
      );
    case 'bim':
    case '3dtiles':
      {
        const modelTree = threeDModelAdapter?.getModelTree?.();
        const pickedSelection =
          threeDModelAdapter?.getLastPickedFeatureSelection?.() ?? null;
        const selection = resolveThreeDSelectionSnapshot(
          sceneSelection,
          pickedSelection,
        );
      return (
        <CardWrapper kind={toolCardKind}>
          <ThreeDModelToolCard
            adapter={threeDModelAdapter ?? null}
            layer={layer}
            selection={selection}
            modelTree={modelTree}
            forceVisible
          />
          <SectionAndClipToolCard
            adapter={threeDModelAdapter?.getSectionAndClipAdapter?.() ?? null}
            forceVisible
          />
        </CardWrapper>
      );
      }
    case 'geophysics':
      // The `geophysics` family also covers `bathymetry`; the
      // BathymetryToolCard activates when the underlying layer kind
      // is `bathymetry`, otherwise the GeophysicsToolCard renders.
      if (layerKind === 'bathymetry' || layer?.kind === ('bathymetry' as Layer['kind'])) {
        return (
          <CardWrapper kind="bathymetry">
            <BathymetryHost layer={layer} container={container} />
          </CardWrapper>
        );
      }
      return (
        <CardWrapper kind="geophysics">
          <GeophysicsHost layer={layer} container={container} />
        </CardWrapper>
      );
    case 'sensor':
      return (
        <CardWrapper kind="sensor">
          <SensorHost layer={layer} />
        </CardWrapper>
      );
    case 'ai':
      return (
        <CardWrapper kind="ai">
          <AIReviewToolCard
            candidates={[]}
            layerLabel={layer?.label ?? layer?.id}
            permission={readOnly ? 'read-only' : 'allowed'}
          />
        </CardWrapper>
      );
    case 'readiness':
      return (
        <CardWrapper kind="readiness">
          <ReadinessToolCard
            report={null}
            layerId={layer?.id ?? null}
            readOnly={readOnly}
          />
        </CardWrapper>
      );
    case 'environment': {
      const envStyle = (layer?.style ?? {}) as Record<string, unknown>;
      const envKind = (envStyle.environmentKind as string) ?? 'sky';
      const envParams = (envStyle.environmentParameters ?? {}) as Record<string, unknown>;
      const envAdapter = pointCloudAdapter as { setEnvironmentParameter?: (layerId: string, key: string, value: unknown) => void } | null;
      return (
        <CardWrapper kind="environment">
          <EnvironmentToolCard
            layerId={layer?.id ?? ''}
            environmentKind={envKind as any}
            parameters={envParams}
            onParameterChange={(key, value) => {
              if (envAdapter?.setEnvironmentParameter && layer?.id) {
                envAdapter.setEnvironmentParameter(layer.id, key, value);
              }
            }}
            onClose={() => clearSceneSelection?.('keyboard')}
          />
        </CardWrapper>
      );
    }
    case 'gtwin':
    default:
      return (
        <CardWrapper kind="empty">
          <EmptyToolCard />
        </CardWrapper>
      );
  }
}

// ---------------------------------------------------------------------------
// Wrapper — owns the canonical `data-testid="tool-card-<kind>"` so
// E2E selectors stay stable regardless of per-card internals.
// ---------------------------------------------------------------------------

interface CardWrapperProps {
  readonly kind: string;
  readonly children: ReactNode;
}

function CardWrapper({ kind, children }: CardWrapperProps) {
  return (
    <div data-testid={`tool-card-${kind}`} style={{ display: 'contents' }}>
      {children}
    </div>
  );
}
